import * as Sentry from '@sentry/react'
import {
  LoaderFunctionArgs,
  redirect,
  ShouldRevalidateFunction,
} from 'react-router'

import { forceLogout } from '@/contexts/auth/force-logout'
import { buildSessionState } from '@/contexts/auth/session/config'
import {
  MissingResourceIdInRouteError,
  ResourceType,
} from '@/errors/route/missing-resource-id'
import { queryClient } from '@/queries'
import { userQueryOptions } from '@/queries/user'
import { getRouteResourceIds } from '@/routes/loaders/helpers/get-route-resource-ids'
import { isForbiddenResourceError } from '@/routes/loaders/helpers/is-forbidden-resource-error'

export const userLoader = async (loaderArgs: LoaderFunctionArgs) => {
  const { companyIdFromPath, companyIdFromQuery } =
    getRouteResourceIds(loaderArgs)

  const companyId = companyIdFromPath || companyIdFromQuery

  if (!companyId) {
    throw new MissingResourceIdInRouteError(
      'Missing company ID in route.',
      ResourceType.COMPANY_ID,
    )
  }

  const { isUserAuthenticated, userId } = buildSessionState()

  if (!isUserAuthenticated || !userId) {
    throw redirect('/login')
  }

  const user = await queryClient
    .query(userQueryOptions(companyId, userId))
    .catch((error: unknown) => {
      if (isForbiddenResourceError(error)) {
        throw redirect('/companies')
      }

      // TODO: Handle throwing a custom error user inactive (not found) error.

      throw error
    })

  // TODO: Throw the custom user inactive error instead of logging out, so the error element shows the inactive screen.
  // Backend returns 404 for inactive users today, so this only runs once it returns the user with active: false.
  if (!user.active) {
    forceLogout()
    throw redirect('/login')
  }

  Sentry.setUser({ id: user._id, email: user.email, username: user.name })

  return null
}

export const userLoaderShouldRevalidate: ShouldRevalidateFunction = ({
  currentParams,
  nextParams,
  defaultShouldRevalidate,
}): boolean =>
  defaultShouldRevalidate || currentParams.companyId !== nextParams.companyId
